import { HttpClient, HttpContext, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { formatDate } from '@angular/common';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../../../../../environments/environment';
import { SHOW_LOADER } from '../../../../../core/interceptors/micro-service-logging.interceptor';
import { UnpaidPaymentsAdapterService } from '../../unpaid-payments-adapter.service';
import { UnpaidPaymentsIsDummyService } from '../../unpaid-payments-is-dummy.service';
import { isUnpaidProductUnauthorized } from '../../unpaid-payments-access.util';
import { partnerProxyKey, partnerProxyUrl } from '../../../../../core/services/partner-proxy';
import {
  DENEFITS_CATEGORIES,
  DenefitsBalanceSnapshot,
  DenefitsCategoryOption,
  DenefitsDashboardSection,
  DenefitsDetailMode,
  DenefitsFundingOption,
  DenefitsView,
  getDenefitsCategoryById,
  isPartnerFilter,
} from './denefits-unpaid.config';

export interface DenefitsListRequest {
  view: DenefitsView;
  filterId: number;
  limit: number;
  offset: number;
  startDate?: string;
  endDate?: string;
  search?: string;
  statusFilter?: string;
  vipOnly?: boolean;
  urgentOnly?: boolean;
  orpFilter?: number;
  instantClaim?: number;
  paymentType?: number;
  challengeType?: number;
  entityId?: string | number | null;
  isExport?: boolean;
  isContractWise?: boolean;
}

export interface DenefitsPayRequest {
  filterId: number;
  view: DenefitsView;
  detailMode: DenefitsDetailMode;
  rows: any[];
  transferSource: 'card' | 'bank_account';
  urgentOnly?: boolean;
  startDate?: string;
  endDate?: string;
}

export interface DenefitsActionRequest {
  filterId: number;
  rows: any[];
  note?: string;
}

export interface DenefitsPayResult {
  success: boolean;
  message: string;
  failures?: any[];
}

export interface DenefitsLendeePayRequest {
  paymentId: number | string;
  paymentTo: number;
  paymentBy: 1 | 2;
}

@Injectable({
  providedIn: 'root',
})
export class DenefitsUnpaidApiService {
  private readonly headers = new HttpHeaders({
    'Content-Type': 'application/json;charset=UTF-8',
  });

  /** Denefits uses in-component loaders; never trigger the global modal loader. */
  private readonly httpContextNoGlobalLoader = new HttpContext().set(SHOW_LOADER, false);

  constructor(
    private readonly http: HttpClient,
    private readonly unpaidAdapter: UnpaidPaymentsAdapterService,
    private readonly isDummyService: UnpaidPaymentsIsDummyService
  ) {}

  getBalance(): Observable<any> {
    return this.post<any>('admin/get_denefits_balance', {
      access_token: this.getAdminToken(),
    });
  }

  getDashboard(startDate?: string, endDate?: string): Observable<any> {
    return this.post<any>('admin/get_unpaid_dashboard_data', {
      access_token: this.getAdminToken(),
      is_dummy: this.getIsDummy(),
      start_date: this.toApiDateString(startDate),
      end_date: this.toApiDateString(endDate),
    });
  }

  listPayments(request: DenefitsListRequest): Observable<any> {
    const endpoint = request.view === 'business' ? 'admin/view_business_unpaid_payments' : 'admin/view_unpaid_payments';
    const payload: Record<string, any> = {
      access_token: this.getAdminToken(),
      is_dummy: this.getIsDummy(),
      type_of_filter: request.filterId,
      limit: request.isExport ? 0 : request.limit,
      offset: request.isExport ? 0 : request.offset,
      active_status: request.statusFilter ?? '0',
      is_vip: request.vipOnly ? 1 : 0,
      is_urgent: request.urgentOnly ? 1 : 0,
      challenge_type: request.filterId === 4 ? Number(request.challengeType ?? 0) : 0,
      search_string: request.search?.trim() || undefined,
      start_date: this.toApiDateString(request.startDate),
      end_date: this.toApiDateString(request.endDate),
      is_export: request.isExport ? 1 : 0,
    };

    if (request.filterId === 2 && request.view === 'listing') {
      payload['is_orp'] = request.orpFilter ?? -1;
      payload['is_orp_instant_claim'] = request.instantClaim ?? -1;
    }

    if (request.filterId === 3 && request.view === 'listing') {
      payload['payment_type'] = Number(request.paymentType ?? 0);
    }

    if (request.view === 'business-details' && request.entityId !== null && request.entityId !== undefined && request.entityId !== '') {
      if (isPartnerFilter(request.filterId)) {
        payload['partner_id'] = request.entityId;
      } else {
        payload['doctor_id'] = request.entityId;
      }
    }

    if (request.isContractWise) {
      payload['is_contract_wise'] = 1;
    }

    return this.post<any>(endpoint, payload);
  }

  listLendeePayments(request: {
    limit: number;
    offset: number;
    shareType?: number;
    paymentStatus?: number;
    search?: string;
  }): Observable<any> {
    return this.post<any>('admin/lendee_unpaid_payments', {
      access_token: this.getAdminToken(),
      is_dummy: this.getIsDummy(),
      limit: request.limit,
      offset: request.offset,
      share_type: request.shareType ?? 0,
      status: request.paymentStatus ?? 0,
      search_string: request.search?.trim() || '',
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'PDT',
    });
  }

  payPayments(request: DenefitsPayRequest): Observable<DenefitsPayResult> {
    return this.post<any>('admin/pay_unpaid_contract', this.buildPaymentPayload(request)).pipe(
      map((res: any) => {
        const failureList = res?.data?.failed_arr_obj?.failerArr;
        if (Array.isArray(failureList) && failureList.length) {
          return {
            success: false,
            message: 'Some payments could not be transferred.',
            failures: failureList,
          };
        }

        return {
          success: true,
          message:
            res?.data?.successArr?.[0]?.success_message ||
            res?.message ||
            'Payment transferred successfully.',
        };
      })
    );
  }

  payLendeeMoney(request: DenefitsLendeePayRequest): Observable<DenefitsPayResult> {
    const form = new URLSearchParams();
    form.set('payment_id', String(request.paymentId));
    form.set('payment_to', String(request.paymentTo));
    form.set('payment_by', String(request.paymentBy));
    form.set('access_token', this.getAdminToken());

    const timezone = sessionStorage.getItem('timezone') || localStorage.getItem('timezone') || '-700';
    const { url, headers } = this.route(
      'admin/pay_lendee_money',
      new HttpHeaders({
        Accept: '*/*',
        'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
        timezone,
        'x-click-id': 'https://testadminv2.denefits.com/app/lendee_offers/unpaid_payments?page=1&search_string=&share_type=0&status=0',
      }),
      false
    );

    return this.http
      .post<any>(url, form.toString(), {
        headers,
        context: this.httpContextNoGlobalLoader,
      })
      .pipe(
        map((res: any) => {
          if (res?.is_error) {
            throw new Error(this.extractErrorMessage(res));
          }
          return {
            success: true,
            message: res?.message || 'Payment transferred successfully.',
          };
        }),
        catchError((error) => throwError(() => new Error(this.extractErrorMessage(error))))
      );
  }

  updateUrgent(request: DenefitsActionRequest, urgent: boolean): Observable<any> {
    return this.post<any>('admin/update_payments_to_urgent', {
      access_token: this.getAdminToken(),
      is_urgent: urgent ? 1 : 0,
      unpaid_filter_id: request.filterId,
      payment_id_arr: this.extractPaymentIds(request.rows),
      note: request.note?.trim() || undefined,
    });
  }

  removeFromUnpaid(request: DenefitsActionRequest): Observable<any> {
    return this.post<any>('admin/block_unblock_payout_payment_v2', {
      access_token: this.getAdminToken(),
      block_unblock_payout_payment: 1,
      type_of_filter: request.filterId,
      payment_ids: this.extractPaymentIds(request.rows),
      note: request.note?.trim() || undefined,
    });
  }

  mapVisibleCategories(response: any): DenefitsCategoryOption[] {
    const listObject = response?.data?.list_obj;
    if (!listObject || typeof listObject !== 'object') {
      return [];
    }

    const visibleFilterIds = new Set<number>();
    for (const sectionKey of Object.keys(listObject)) {
      const rawItems = listObject[sectionKey];
      for (const item of Object.values(rawItems || {})) {
        const filterId = Number((item as any)?.unpaid_filter_id);
        const totalPayments = Number((item as any)?.total_payments ?? 0);
        if (totalPayments > 0 && getDenefitsCategoryById(filterId)) {
          visibleFilterIds.add(filterId);
        }
      }
    }

    return DENEFITS_CATEGORIES.filter((category) => visibleFilterIds.has(category.id));
  }

  mapDashboardSections(response: any): DenefitsDashboardSection[] {
    const listObject = response?.data?.list_obj;
    if (!listObject || typeof listObject !== 'object') {
      return [];
    }

    return Object.keys(listObject)
      .map((sectionKey) => {
        const rawItems = listObject[sectionKey];
        const rows = Object.values(rawItems || {})
          .flatMap((item: any) => {
            const category = getDenefitsCategoryById(Number(item?.unpaid_filter_id));
            if (!category || Number(item?.total_payments ?? 0) <= 0) {
              return [];
            }

            return [
              {
                unpaid_filter_id: Number(item?.unpaid_filter_id),
                category: String(item?.category || category.key),
                label: category.label,
                tone: category.tone,
                businesses: Number(item?.total_businesses ?? 0),
                unpaidPayments: Number(item?.total_payments ?? 0),
                urgentCount: Number(item?.total_urgent_payments ?? 0),
                amount: Number(item?.total_amount_to_pay ?? 0),
              },
            ];
          })
          .sort((a, b) => a.label.localeCompare(b.label));

        return {
          key: sectionKey,
          title: this.formatSectionTitle(sectionKey),
          rows,
        };
      })
      .filter((section) => section.rows.length > 0);
  }

  mergeBalance(current: DenefitsBalanceSnapshot, incoming?: any): DenefitsBalanceSnapshot {
    const next = incoming || {};
    return {
      available_balance: this.toNumber(next?.available_balance, current.available_balance),
      upcoming_balance: this.toNumber(next?.upcoming_balance, current.upcoming_balance),
      total_amount_to_pay: this.toNumber(next?.total_amount_to_pay, current.total_amount_to_pay),
      urgent_balance: this.toNumber(next?.urgent_balance, current.urgent_balance),
      total_urgent_payments: this.toNumber(next?.total_urgent_payments, current.total_urgent_payments),
      available_balance_by_source: {
        card: this.toNumber(next?.available_balance_by_source?.card, current.available_balance_by_source.card),
        bank_account: this.toNumber(
          next?.available_balance_by_source?.bank_account ?? next?.available_balance_by_source?.bank,
          current.available_balance_by_source.bank_account
        ),
      },
    };
  }

  buildFundingOptions(balance: DenefitsBalanceSnapshot): DenefitsFundingOption[] {
    return [
      {
        label: `Card ($${balance.available_balance_by_source.card.toFixed(2)})`,
        value: 'card',
        amount: balance.available_balance_by_source.card,
      },
      {
        label: `Bank ($${balance.available_balance_by_source.bank_account.toFixed(2)})`,
        value: 'bank_account',
        amount: balance.available_balance_by_source.bank_account,
      },
    ];
  }

  parseSummary(summary: any): any | null {
    if (!summary || typeof summary !== 'object') {
      return null;
    }

    const parsed = { ...summary };
    parsed['contract_data'] = this.safeJsonParse(parsed['contract_data'], parsed['contract_data'] ?? null);
    parsed['last_3_payouts'] = this.safeJsonParse(parsed['last_3_payouts'], []);
    if (Array.isArray(parsed['last_3_payouts'])) {
      parsed['last_3_payouts_total'] = parsed['last_3_payouts'].reduce(
        (sum: number, payout: any) => sum + this.toNumber(payout?.amount),
        0
      );
    } else {
      parsed['last_3_payouts'] = [];
      parsed['last_3_payouts_total'] = 0;
    }

    parsed['multiple_active_status'] =
      parsed['partner_active_status'] ||
      parsed['business_active_status'] ||
      '';

    return parsed;
  }

  parseNotes(raw: unknown): Array<{ note: string; date_added?: string; added_by?: string }> {
    const parsed = this.safeJsonParse(raw, []);
    if (!Array.isArray(parsed)) {
      return [];
    }

    return parsed
      .map((note: any) => ({
        note: String(note?.note || note?.notes || note?.reason || '').trim(),
        date_added: note?.date_added ? formatDate(note.date_added, 'MM/dd/yyyy h:mm a', 'en-US') : undefined,
        added_by: note?.added_by || note?.admin_name || undefined,
      }))
      .filter((note) => !!note.note)
      .sort((a, b) => String(b.date_added || '').localeCompare(String(a.date_added || '')));
  }

  parseRawNotes(raw: unknown): unknown[] {
    const parsed = this.safeJsonParse(raw, []);
    return Array.isArray(parsed) ? parsed : [];
  }

  private buildPaymentPayload(request: DenefitsPayRequest): Record<string, any> {
    const rows = Array.isArray(request.rows) ? request.rows : [];
    const partner = isPartnerFilter(request.filterId);
    const entityKey = partner ? 'partner_id' : 'doctor_id';
    const paymentMethod = request.transferSource === 'bank_account' ? 1 : 2;
    const body: Record<string, any> = {
      access_token: this.getAdminToken(),
      unpaid_filter_id: request.filterId,
      payment_method: paymentMethod,
      is_urgent: request.urgentOnly ? 1 : undefined,
      start_date: this.toApiDateString(request.startDate),
      end_date: this.toApiDateString(request.endDate),
    };

    if (request.urgentOnly && !rows.length) {
      return this.cleanPayload(body);
    }

    if (request.view === 'business') {
      body[entityKey] = this.unique(rows.map((row) => row?.[entityKey]));
      return this.cleanPayload(body);
    }

    if (request.filterId === 3) {
      const subscriptionIds = this.unique(rows.map((row) => row?.subscription_payment_process_id));
      const processIds = this.unique(rows.map((row) => row?.payment_process_id));
      if (subscriptionIds.length) body['pp_subscription_id'] = subscriptionIds;
      if (processIds.length) body['ppr_id'] = processIds;
      if (!subscriptionIds.length && !processIds.length) {
        body['payment_id_arr'] = this.extractPaymentIds(rows);
      }
      return this.cleanPayload(body);
    }

    if (request.view === 'business-details' && request.detailMode === 'contract') {
      const contractIds = this.unique(rows.map((row) => row?.contract_id));
      if (contractIds.length) {
        body['contract_id'] = contractIds;
        body[entityKey] = this.unique(rows.map((row) => row?.[entityKey]));
        return this.cleanPayload(body);
      }
    }

    body['payment_id_arr'] = this.extractPaymentIds(rows);
    return this.cleanPayload(body);
  }

  private extractPaymentIds(rows: any[]): Array<number | string> {
    return this.unique(
      rows.map((row) => row?.payment_id ?? row?.id).filter((value) => value !== undefined && value !== null && value !== '')
    );
  }

  private post<T>(path: string, payload: Record<string, any>): Observable<T> {
    const cleaned = this.cleanPayload(payload);
    const timezoneName = localStorage.getItem('timezoneName') || '';
    const { url, headers } = this.route(
      path,
      this.headers.set('timezoneName', timezoneName),
      Boolean(cleaned['is_export'])
    );

    return this.http
      .post<T>(url, cleaned, {
        headers,
        context: this.httpContextNoGlobalLoader,
      })
      .pipe(
      map((res: any) => {
        if (isUnpaidProductUnauthorized(res)) {
          return res;
        }
        if (res?.is_error) {
          throw new Error(this.extractErrorMessage(res));
        }
        return res;
      }),
      catchError((error) => throwError(() => new Error(this.extractErrorMessage(error))))
    );
  }

  private buildBaseUrl(isExport: boolean): string {
    if (isExport && environment.production) {
      return 'https://apilive.denefits.com/exports/';
    }
    const fromGetSetting = this.unpaidAdapter.getPartnerApiBaseUrlFromSettings('denefits', 'denefits');
    const fromLocalStorage = localStorage.getItem('baseUrl')?.trim() || '';
    const configuredBaseUrl =
      environment.productServerUrls.find((server) => String(server?.name || '').toLowerCase() === 'denefits')?.url?.trim() ||
      '';
    const fromEmbed = String(environment.product_postMessage?.DENEFITS?.server_url || '').trim();

    // Prefer `/auth/get-setting` (`dynamic_routes`) so admin-configured host wins over stale localStorage.
    const candidate =
      fromGetSetting || fromLocalStorage || configuredBaseUrl || fromEmbed;

    if (!candidate) {
      console.warn(
        'DenefitsUnpaidApiService: no Denefits base URL from get-setting, localStorage, or environment.productServerUrls'
      );
      return this.ensureTrailingSlash('https://api.denefits.com');
    }

    return this.ensureTrailingSlash(candidate);
  }

  private ensureTrailingSlash(url: string): string {
    return url.endsWith('/') ? url : `${url}/`;
  }

  /**
   * Where a Denefits call goes. With prism-new routing on it goes through prism-new's partner proxy,
   * which adds the partner credentials server-side, so only the prism session is sent from here.
   * Exports sit beside the partner's stored base, not beneath it; `partnerProxyUrl` sends those as a
   * host-root (`~/`) path, so they proxy too and never need the credentials in the browser.
   */
  private route(path: string, headers: HttpHeaders, isExport: boolean): { url: string; headers: HttpHeaders } {
    const baseUrl = this.buildBaseUrl(isExport);
    const proxyKey = partnerProxyKey('denefits');
    if (!proxyKey) {
      return { url: `${baseUrl}${path}`, headers: this.withPartnerAuthHeaders(headers) };
    }
    const partnerBase = this.unpaidAdapter.getPartnerApiBaseUrlFromSettings('denefits', 'denefits');
    const token = sessionStorage.getItem('access_token');
    return {
      url: partnerProxyUrl(proxyKey, `${baseUrl}${path}`, partnerBase),
      headers: token ? headers.set('Authorization', `Bearer ${token}`) : headers,
    };
  }

  /** Session / dynamic_routes credentials: authorization, secret_key, email (same sources as UnpaidPaymentsAdapterService). */
  private withPartnerAuthHeaders(headers: HttpHeaders): HttpHeaders {
    const partner = this.unpaidAdapter.getPartnerAuthHeaders('denefits', 'denefits');
    let next = headers;
    const token = partner['authorization']?.trim();
    if (token) {
      next = next.set('authorization', token);
    }
    const secret = partner['prism-secret-key']?.trim();
    if (secret) {
      next = next.set('prism-secret-key', secret);
    }
    const email = partner['email']?.trim();
    if (email) {
      next = next.set('email', email);
    }
    return next;
  }

  private getAdminToken(): string {
    return (
      localStorage.getItem('adminToken') ||
      sessionStorage.getItem('adminToken') ||
      ''
    );
  }

  private getIsDummy(): string {
    return this.isDummyService.getStorageValue();
  }

  /** Denefits APIs expect calendar dates as `YYYY-MM-DD` (same as legacy `toLocaleDateString('en-CA')`). */
  private toApiDateString(value?: string): string | undefined {
    if (!value?.trim()) {
      return undefined;
    }
    const trimmed = value.trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      return trimmed;
    }
    const parsed = new Date(trimmed);
    if (Number.isNaN(parsed.getTime())) {
      return trimmed.length >= 10 ? trimmed.slice(0, 10) : undefined;
    }
    return parsed.toLocaleDateString('en-CA');
  }

  private formatSectionTitle(key: string): string {
    return String(key || '')
      .toLowerCase()
      .split('_')
      .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
      .join(' ');
  }

  private cleanPayload<T extends Record<string, any>>(payload: T): Partial<T> {
    const frontendOnlyKeys = new Set(['show_loader', 'showLoader', 'loaderMessage']);
    return Object.entries(payload).reduce((acc, [key, value]) => {
      if (
        frontendOnlyKeys.has(key) ||
        value === undefined ||
        value === null ||
        value === '' ||
        value === 'undefined' ||
        value === 'null'
      ) {
        return acc;
      }
      (acc as any)[key] = value;
      return acc;
    }, {} as Partial<T>);
  }

  private extractErrorMessage(error: any): string {
    return (
      error?.message ||
      error?.error?.message ||
      error?.error?.err ||
      error?.err ||
      'Unable to complete the request.'
    );
  }

  private safeJsonParse(value: unknown, fallback: any): any {
    if (value === undefined || value === null || value === '') {
      return fallback;
    }
    if (typeof value !== 'string') {
      return value;
    }
    try {
      return JSON.parse(value);
    } catch {
      return fallback;
    }
  }

  private toNumber(value: unknown, fallback = 0): number {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  private unique<T>(values: T[]): T[] {
    return Array.from(new Set(values.filter((value) => value !== undefined && value !== null && value !== '') as T[]));
  }
}
